class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        /**
         * @param note {string}
         */
        const updateNote = (note) => {
          // Disable note saving
          // let body = JSON.stringify({ note });
          // fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        };

        let deliveryCode = '';
        const submitBtn = document.getElementById('checkout');
        const cartNote = document.getElementById('Cart-note');
        const deliverySelector = document.getElementById('delivery-code');
        const exchangeToggle = document.getElementById('exchangeCheck');

        if (deliverySelector && typeof window.$ === 'function') {
          const $delivery = window.$('#delivery-code');
          if ($delivery && typeof $delivery.on === 'function') {
            deliveryCode = 'ARVARV';
            $delivery.on('select2:select', (e) => {
              deliveryCode = `ARV${e.target.value}ARV`;
            });
          }
        }

        if (submitBtn && cartNote) {
          submitBtn.addEventListener('click', () => {
            let v = cartNote.value ?? '';
            let mutated = false;

            if (deliveryCode && !/ARV\d*ARV/.test(v)) {
              v += deliveryCode;
              mutated = true;
            }

            if (exchangeToggle?.checked) {
              v += 'EXT';
              mutated = true;
            }

            if (mutated) {
              cartNote.style.color = 'transparent';
              cartNote.value = v;
            }
          });
        }

        if (cartNote) {
          this.addEventListener(
            'input',
            debounce(() => {
              updateNote(cartNote.value);
            }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }
      }
    }
  );
}

const CartRewardController = (() => {
  const parseMoneyToCents = (text) => {
    if (!text) return 0;
    const cleaned = text.replace(/[^\d.,]/g, '').replace(/,/g, '');
    if (!cleaned) return 0;
    const parts = cleaned.split('.');
    let cents = parseInt(parts[0] || '0', 10) * 100;
    if (parts[1]) {
      const decimals = parts[1].padEnd(2, '0').slice(0, 2);
      cents += parseInt(decimals, 10);
    }
    return Number.isFinite(cents) ? cents : 0;
  };

  const setPosition = (container, key, value, max) => {
    const el = container.querySelector(`[data-reward-step="${key}"]`);
    if (!el) return;
    const pos = max > 0 ? Math.min(100, Math.max(0, (value * 100) / max)) : 0;
    el.style.setProperty('--reward-position', `${pos}%`);
  };

  const updateRewardBar = () => {
    const rewardEl = document.getElementById('cart-footer-reward');
    const totalEl = document.querySelector('#main-cart-footer .totals__total-value');
    if (!rewardEl || !totalEl) return;

    const totalCents = parseMoneyToCents(totalEl.textContent || '');
    const captions = rewardEl.querySelectorAll('.cart-reward__caption');
    const thresholdFromCaption = (index) => parseMoneyToCents(captions[index]?.textContent || '');

    const shipping = Number(rewardEl.dataset.freeShipping || 0) || thresholdFromCaption(0);
    const gift = Number(rewardEl.dataset.freeGift || 0) || thresholdFromCaption(1);
    const discount10 = Number(rewardEl.dataset.discount10 || 0) || thresholdFromCaption(2);
    const discount15 = Number(rewardEl.dataset.discount15 || 0) || thresholdFromCaption(3);

    const exclusiveMax = Math.max(gift, discount10, discount15, 0);
    const barMax = exclusiveMax > 0 ? exclusiveMax : Math.max(shipping, 1);
    const percent = Math.min(100, Math.max(0, (totalCents * 100) / barMax));

    const fillEl = rewardEl.querySelector('.cart-reward__fill');
    if (fillEl) {
      fillEl.style.setProperty('--cart-progress-value', `${percent}%`);
      fillEl.style.width = `${percent}%`;
    }

    const steps = rewardEl.querySelectorAll('.cart-reward__step');
    steps.forEach((step) => step.classList.remove('is-complete'));

    setPosition(rewardEl, 'shipping', shipping, barMax);
    setPosition(rewardEl, 'gift', gift, barMax);
    setPosition(rewardEl, 'discount10', discount10, barMax);
    setPosition(rewardEl, 'discount15', discount15, barMax);

    if (shipping && totalCents >= shipping) {
      steps[0]?.classList.add('is-complete');
    }

    if (discount15 && totalCents >= discount15) {
      steps[3]?.classList.add('is-complete');
    } else if (discount10 && totalCents >= discount10) {
      steps[2]?.classList.add('is-complete');
    } else if (gift && totalCents >= gift) {
      steps[1]?.classList.add('is-complete');
    }
  };

  const bootstrap = () => {
    updateRewardBar();
  };

  return {
    bootstrap,
    updateRewardBar,
  };
})();

document.addEventListener('DOMContentLoaded', CartRewardController.bootstrap);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector && event.target.querySelector('#cart-footer-reward')) {
    CartRewardController.updateRewardBar();
  }
});

if (typeof subscribe === 'function') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    CartRewardController.updateRewardBar();
  });
}
